<script setup>
import * as Sentry from "@sentry/vue";
import * as amplitude from "@amplitude/analytics-browser";

import { inject, onBeforeUnmount, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import Cookie from "js-cookie";
import { getAvailableCamera, VideoAuthenticator } from "../utils/authenticator.js";
import getInstructionsManager from "../utils/getInstructionsManager";
import sendEvent from "../utils/sendEvent";
import globalApiResolver from "../utils/api";
import { postQueryScan } from "../utils/services.js";
import ChangeCameraModal from "./ChangeCameraModal.vue";
import VideoAuthRetryModal from "./VideoAuthRetryModal.vue";
import CameraAccessModal from "./CameraAccessModal.vue";
import Unreadable from "./Unreadable.vue";
import ThirdPartyCodeResult from "./ThirdPartyCodeResult.vue";
import DataLogger from "../utils/datalogger.js";
import OrientationModal from "./OrientationModal.vue";
import Launch from "./Launch.vue";
import AnalyzingScreen from "@/components/AnalyzingScreen.vue";
import ErrorModal from "@/components/ErrorModal.vue";
import Navbar from "./Navbar.vue";
import Footer from "./Footer.vue";
import "./styles/video.css";

import SentryTransactionWrapper from "../utils/sentry.js";
import { makeId } from "../utils/uniqueId.js";
import { qr_patters_polygon, square_mask_polygon, square_mask_polygon2, square_mask_polygon3 } from "../utils/overlays.js";
import { DeviceInfo } from "../utils/deviceInfo.js";
import { shouldUseBrowserCache, hasSharedArrayBuffer } from "../utils/browser.js";
import IphoneModelsPicker from "./IphoneModelsPicker.vue";
import iphoneModels from "../utils/iphoneModels.js";
import withTimeLimit from "../utils/asyncHelpers.js";
import { sendVideoAuthToNative } from "../utils/nativeBridge";

const __transaction__ = new SentryTransactionWrapper();
let cameraSpan;
const scanningURL = window.location.href;


function getInitialAppVersion() {
  if (window.localStorage) {
    const storedVersion = window.localStorage.getItem("__INITIAL__APP_VERSION__");
    if (storedVersion) {
      return storedVersion;
    } else {
      window.localStorage.setItem("__INITIAL__APP_VERSION__", __APP_VERSION__);
      return __APP_VERSION__;
    }
  }
  return __APP_VERSION__;
}

const __INITIAL__APP_VERSION__ = getInitialAppVersion();

const GLOBAL_TIMEOUT_MS = 30000;

const props = defineProps({
  queryScanId: String,
  deviceInfo: DeviceInfo,
  hasWasmCrashed: Boolean
});

const appConfig = {
  name: "Scantrust Web Video Auth",
  key: props.deviceInfo?.isWechatMiniProgram() ? "wechat-mp-video-auth" : "web-video-auth",
  version: __APP_VERSION__
};

let timer = -1;
let authenticator;
let scanDoneBarrierOn = false;
let isRetry = !!Cookie.get("__st.rt__");
let message = ref("");
let isLoading = ref();
let progress = ref(0);
let isCameraReady = ref(false);
let isChangeCameraModalVisible = ref(false);
let isVideoRetryModalVisible = ref(false);
let isCameraAccessModalVisible = ref(false);
let isUnreadableModalVisible = ref(false);
let availableCameras = ref([]);
let selectedCamera = ref();
let isResetCamera = false;
let hasTorch = ref(false);
let isTorchEnabled = ref(false);
let isThirdPartyCode = ref(false);
const desiredCameraType = ref("");
const isCameraOpening = ref(false);
const isSgInside = ref(true);
const isScanning = ref(true);
const thirdPartyResult = ref("");
const thirdPartyResultTimeStamp = ref();
const installId = ref({});
const coordinates = ref({});
const deviceHeader = ref("");
const prefix = ref({});
const settings = ref({});
const exitURL = ref("");
const product = ref({});
const brand = ref({});
const counter = ref(isRetry ? 5 : 0);
const i18n = useI18n();
const sessionId = makeId(16);
let lastScannedCodeDetails = null;
const scansSessionHint = ref(props.queryScanId);
const isAuthenticating = ref(false);
const videoPreviewImage = ref("");
const uploading = ref(false);
const isResponseError = ref(false);
const showIphoneModels = ref(false);
const skipIphoneModelsPopup = ref(false);
const errorCode = ref("");
const thirdPartyCodeResultRef = ref(null);
const brandingTheme = inject("brandingTheme");
const isIos = props.deviceInfo?.isIos();

const wechatInitializeClick = async () => {
  await nextTick();
  await thirdPartyCodeResultRef.value?.playVideo();
};

window.amplitudeCommonProperties = {
  scanApp: import.meta.env.VITE_SCAN_APP_NAME,
  sessionId,
  scanningURL,
  attemptId: 0
};
window.amplitudeUserProperties = {};

try {
  const url = new URL(window.location.href);
  const settingsParam = url.searchParams.get("settings");
  if (settingsParam) {
    const base64String = decodeURIComponent(settingsParam);
    const jsonString = atob(base64String);
    const decodedData = JSON.parse(jsonString);
    window.amplitudeUserProperties = {
      ...decodedData
    };
  }
} catch (err) {
  console.error("Error parsing settings", err);
  Sentry.captureException(new Error("Error parsing settings", { cause: err }));
}

const handleVisibilityChange = () => {
  // Reset scansSessionHint
  if (document.visibilityState === "hidden") {
    scansSessionHint.value = null;
  }
};

document.addEventListener("visibilitychange", handleVisibilityChange);

onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

function isFrameUploadEnabled() {
  // true or false depending on if frames to be uploaded to s3.
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  if (urlParams.has("no-upload")) {
    return false;
  }
  let DEBUG_FRAME_UPLOAD = import.meta.env.VITE_DEBUG_FRAME_UPLOAD;
  return DEBUG_FRAME_UPLOAD === "true";
}

function shouldDisableTorchAtStart() {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.has("no-torch");
}

console.log(import.meta.env.MODE);
let dataLogger = new DataLogger(sessionId, isFrameUploadEnabled());

Sentry.setContext("session", { id: sessionId });
console.log("Session ID:", sessionId);

if (!isRetry) {
  Cookie.set("__st.rt__", "1", {
    path: "/",
    expires: 365 * 20
  });
}

function saveCache(key, value) {
  if (!window.localStorage) {
    return false;
  }

  localStorage.setItem(key, value);
}

function readCache(key) {
  if (!window.localStorage) {
    return false;
  }

  return localStorage.getItem(key);
}

async function getUsableDeviceWithCache() {
  const cacheKey = "__st.ud__";
  const version = "2"; // bumped to invalidate caches holding the pre-fix camera-selection order

  try {
    if (shouldUseBrowserCache(props.deviceInfo.getBrowser())) {
      const cameras = readCache(cacheKey);
      if (cameras) {
        const cache = JSON.parse(cameras);
        if (cache.version === version && cache.data && cache.data.length) {
          return cache.data;
        }
      }
    }

    const usableDevices = await getAvailableCamera(props.deviceInfo.isIos());
    if (usableDevices.length > 0) {
      saveCache(cacheKey, JSON.stringify({ data: usableDevices, version: version }));
      return usableDevices;
    }
  } catch (err) {
    return Promise.reject(new Error(err));
  }
}

const getDeviceHeader = function () {
  /* Possible fields for this header
    model: The device model number (e.g. SM-G650)
    app: The app name (replace x-scantrust-app header)
    lang: The user language
    sdk-version:
    os:
    os-version:
  */
  let header = `app=${appConfig.key};`;
  const deviceInfo = props.deviceInfo;
  if (deviceInfo?.model) {
    header += `model=${deviceInfo?.model};os=${deviceInfo?.os};os_version=${deviceInfo?.osVersion};browser=${deviceInfo?.browser};browser_version=${deviceInfo?.browserVersion};browser_engine=${deviceInfo?.browserEngine}`;
  }
  return header;
};

function drawImageOnCanvas(canvas_context, image) {
  let array = image.data;
  let width = image.width;
  let canvas_context_image = canvas_context.getImageData(0, 0, image.width, image.height);
  let image_data = canvas_context_image.data;
  let v,
    indexOut = 0,
    indexIn = 0,
    indexOutR = 0,
    indexInR = 0;
  let bpr = image.bytesPerRow;
  for (let r = 0; r < image.height; ++r) {
    indexOut = indexOutR;
    indexIn = indexInR;
    for (let c = 0; c < image.width; ++c) {
      v = array[indexIn];
      image_data[indexOut] = v;
      image_data[indexOut + 1] = v;
      image_data[indexOut + 2] = v;
      image_data[indexOut + 3] = 255;
      indexOut += 4;
      indexIn += 1;
    }
    indexOutR += 4 * width;
    indexInR += bpr;
  }
  canvas_context.putImageData(canvas_context_image, 0, 0);
}

const imageToBlob = function (image) {
  let width = image.width;
  let height = image.height;

  let cvs = document.createElement("canvas");
  cvs.width = width;
  cvs.height = height;

  let ctx = cvs.getContext("2d");
  drawImageOnCanvas(ctx, image);

  const syncWrapper = function () {
    return new Promise((resolve, reject) => {
      cvs.toBlob(
        function (blob_) {
          cvs.remove();
          resolve(blob_);
        },
        "image/jpeg",
        1
      );
    });
  };

  return syncWrapper();
};

async function sendAuthenticationCandidate(candidate, comesFromUnreadableFlow) {
  /**
   * Request an authentication on the provided candidate. Emit the proper (UI) event
   * based on the response.
   *
   * /!\ The event handler will have to release the URL object `data.imageDataURl` once used.
   * URL.revokeObjectURL(data.imageDataURL)
   */
  isAuthenticating.value = true;
  authenticator.setTorch({ isEnabled: false, saveState: false });

  let secureCode = candidate.getSecureCode();
  let parsedContent = secureCode.getParsedContent();
  let codeOrigin = secureCode.getOrigin();
  let image = candidate.getImage();
  let previewImage = candidate.getSecureCodeImage();
  let cropOffset = image.roi;
  const host = codeOrigin.getApiHost();

  console.log("host", host);
  const data = {
    // image: Image  set later

    extended_id: parsedContent.getCodeID(), // ID of the scanned code
    custom_key: parsedContent.getCustomKey(), // external ID
    code_space: codeOrigin.getCodeSpace(), // company code space

    unreadable: comesFromUnreadableFlow, // True if the scan doesn't come from
    blur_score: candidate.getBlurScore(),

    // set later
    // "qr_patterns_centers[...]": float 	    // p0.x

    // set later
    // "crop_offset[...]": int

    with_optic: false, // is the scan made with an optic

    "device.model": props.deviceInfo.model,
    "device.os": props.deviceInfo.os,
    "device.os_version": props.deviceInfo.osVersion,
    "device.language": window.navigator.language,
    "device.user_agent": window.navigator.userAgent,

    "location.positioning": 0,

    mobile_scan_timestamp: candidate.getFrameTimestamp(),

    mobile_app_version: appConfig.version
  };

  if (coordinates.value?.latitude && coordinates.value?.longitude) {
    data["location.latitude"] = coordinates.value.latitude;
    data["location.longitude"] = coordinates.value.longitude;
    data["location.positioning"] = coordinates.value?.accuracy <= 50 ? 4 : 2;
  }

  let formData = new FormData();
  for (let k in data) {
    formData.append(k, data[k]);
  }

  let points = secureCode.getPatterns();
  for (let i = 0; i < points.size(); ++i) {
    let p = points.get(i);
    console.log(`${i}: ${p}(${p.x}, ${p.y})`);
    formData.append("qr_patterns_centers[" + i * 2 + "]", p.x);
    formData.append("qr_patterns_centers[" + (i * 2 + 1) + "]", p.y);
  }

  formData.append("crop_offset[0]", cropOffset.x0);
  formData.append("crop_offset[1]", cropOffset.y0);

  let blobPromise = imageToBlob(image);
  let bestFrameId = candidate.getFrameId();
  let apiHost = globalApiResolver.getApiDomain(host);
  let blobPreviewImage = imageToBlob(previewImage ?? image);

  blobPreviewImage
    .then((blob) => {
      if (!blob) {
        console.warn("Preview image blob is empty");
        Sentry.captureException(new Error("Preview image blob is empty"));
        return;
      }
      if (videoPreviewImage.value) {
        URL.revokeObjectURL(videoPreviewImage.value);
      }
      videoPreviewImage.value = URL.createObjectURL(blob);
    })
    .catch((err) => {
      console.error("Failed to create the preview image URL", err);
      Sentry.captureException(new Error("Failed to create the preview image URL", { cause: err }));
    });

  blobPromise.then((blob) => {
    formData.append("image", blob, "image.jpg");
    dataLogger.authTimeStamp = Date.now();
    fetch(`https://${apiHost}/api/scan/verify/`, {
      method: "POST",
      headers: {
        "X-SCANTRUST-APP": appConfig.key,
        "x-scantrust-device": getDeviceHeader(),
        "X-Scantrust-Install-Id": installId.value,
        ...(scansSessionHint.value ? { "X-Scantrust-Session-Hint": scansSessionHint.value } : {})
      },
      body: formData
    })
      .then(async (resp) => {
        if (resp.status === 200) {
          return resp.json();
        } else if (resp.status == 404) {
          return Promise.reject(new Error("code_not_found", { cause: resp }));
        } else if (resp.status >= 500 && resp.status < 600) {
          errorCode.value = resp.status;
          isResponseError.value = true;
          return Promise.reject(new Error("server_error", { cause: resp }));
        } else {
          console.log("Status: " + resp.status);
          const msg = await resp.json();
          console.log("Response: " + msg);
          return Promise.reject(new Error("server", { cause: resp }));
        }
      })
      .then((dataJson) => {
        dataLogger.recordTimeResponseReceived();
        if (!scansSessionHint.value) {
          scansSessionHint.value = dataJson.uuid?.toString();
        }
        console.log("Auth success: ");
        dataLogger.logScanData(
          dataJson?.uuid,
          props.deviceInfo?.model,
          !settings.value?.use_defaults,
          props.deviceInfo?.browser,
          props.deviceInfo?.browserVersion,
          props.deviceInfo?.browserEngine,
          window.location.href,
          appConfig?.version,
          availableCameras.value,
          authenticator.getCamera()?.capabilities,
          authenticator.getCamera()?.getVideoTrackSettings(),
          dataJson?.result,
          bestFrameId,
          dataJson?.auth_failure_mode,
          comesFromUnreadableFlow ? "timeoutWithCandidate" : "candidateFound"
        );
        Sentry.setContext("scan", { uuid: dataJson["uuid"], apiHost, host });

        for (let k in dataJson) {
          console.log("  " + k + ": " + dataJson[k]);
        }
        dataJson["content"] = parsedContent.getRawContent();
        sendEvent("AUTH_COMPLETE", dataJson);
      })
      .catch((err) => {
        dataLogger.recordTimeResponseReceived();
        uploading.value = false;
        if (err.type === "code_not_found") {
          sendEvent("CODE_NOT_FOUND");
        } else if (err.cause?.status === 500 || err.response?.status === 500) {
          isResponseError.value = true;
        } else {
          sendEvent("ERROR", { type: "http", response: err.data });
        }
        console.log("Auth success:  error:", err);
      })
      .finally(() => {
        points.delete();
        parsedContent.delete();
        codeOrigin.delete();
        secureCode.delete();
        image.delete();
        candidate.delete();
      });
  });
}

function sessionTimeoutOccurred(timeoutType) {
  // timeoutType: str "global" or "sdk"

  dataLogger.logScanData(
    null,
    props.deviceInfo?.model,
    !settings.value?.use_defaults,
    props.deviceInfo?.browser,
    props.deviceInfo?.browserVersion,
    props.deviceInfo?.browserEngine,
    window.location.href,
    appConfig?.version,
    availableCameras.value,
    authenticator.getCamera()?.capabilities,
    authenticator.getCamera()?.getVideoTrackSettings(),
    null,
    null,
    null,
    timeoutType === "global" ? "globalTimeout" : "timeoutWithoutCandidate"
  );

  resetOverlay();
  authenticator.setTorch({ isEnabled: false, saveState: false });

  openVideoRetryModal();
}

function getDefaultUserMessage(event, instructionManager) {
  switch (event.detail.key) {
    case "NO_CODE_IN_FRAME":
      progress.value = 25;
      message.value = "scan.align";
      amplitude.track("Align the pattern", { ...window.amplitudeCommonProperties, code_state: "NO_CODE_IN_FRAME" });
      break;
    case "CODE_TOO_SMALL":
      progress.value = 50;
      message.value = "scan.closer";
      amplitude.track("Slowly move closer", { ...window.amplitudeCommonProperties, code_state: "CODE_TOO_SMALL" });
      break;
    case "CODE_TOO_BIG":
      progress.value = 50;
      message.value = "scan.away";
      amplitude.track("Slowly move away", { ...window.amplitudeCommonProperties, code_state: "CODE_TOO_BIG" });
      break;
    case "CODE_IS_BLURRY":
      progress.value = 50;
      message.value = "scan.focus";
      amplitude.track("Move slightly to focus", { ...window.amplitudeCommonProperties, code_state: "CODE_IS_BLURRY" });
      break;
    case "FRAME_OK":
      window.clearTimeout(timer); // Clear the global timeout and rely only on the SDK one. The timer is not set to -1 here.
      progress.value = 75;
      message.value = "scan.upload";
      instructionManager.updateLastDisplayTime();
      amplitude.track("Stay still", { ...window.amplitudeCommonProperties, code_state: "FRAME_OK" });
      break;
  }
}

function getQRUserMessage(event, instructionManager) {
  switch (event.detail.key) {
    case "NO_CODE_IN_FRAME":
      progress.value = 25;
      message.value = "scan.align";
      amplitude.track("Center the QR", { ...window.amplitudeCommonProperties, code_state: "NO_CODE_IN_FRAME" });
      break;
    case "CODE_TOO_SMALL":
      progress.value = 50;
      message.value = "scan.closer";
      amplitude.track("Slowly move closer", { ...window.amplitudeCommonProperties, code_state: "CODE_TOO_SMALL" });
      break;
    case "CODE_IS_BLURRY":
      progress.value = 50;
      message.value = "scan.focus";
      amplitude.track("Move slightly to focus", { ...window.amplitudeCommonProperties, code_state: "CODE_IS_BLURRY" });
      break;
    case "CODE_TOO_BIG":
      progress.value = 50;
      message.value = "scan.away";
      amplitude.track("Slowly move away", { ...window.amplitudeCommonProperties, code_state: "CODE_TOO_BIG" });
      break;
    case "FRAME_OK":
      window.clearTimeout(timer); // Clear the global timeout and rely only on the SDK one. The timer is not set to -1 here.
      progress.value = 75;
      message.value = "scan.upload";
      instructionManager.updateLastDisplayTime();
      amplitude.track("Stay still", { ...window.amplitudeCommonProperties, code_state: "FRAME_OK" });
      break;
  }
}

function handleSDKFeedbackEvent(event, instructionManager) {
  __transaction__.makeSpan({
    op: "frame",
    name: event.detail.key
  });

  dataLogger.frameData.codeState = event.detail.key;
  dataLogger.frameData.selectedCameraIndex = authenticator.getSelectedCameraId();
  dataLogger.frameData.isTorchEnabled = isTorchEnabled.value;

  if (event.detail.key !== "FRAME_OK" && !instructionManager.showInstructions()) {
    // return if we should not show instructions
    dataLogger.addFrameData();
    return;
  }

  if (!settings.value.use_defaults) {
    getQRUserMessage(event, instructionManager);
  } else {
    getDefaultUserMessage(event, instructionManager);
  }

  dataLogger.frameData.messageToUser = message.value;
  dataLogger.addFrameData();
}

function startGlobalTimeoutTimer() {
  if (timer === -1) {
    timer = window.setTimeout(() => {
      timer = -1;
      dataLogger.recordTimeAuthCandidate();
      authenticator.stopProcessing();
      sendEvent("TIMEOUT", { type: "global" });
    }, GLOBAL_TIMEOUT_MS);
  }
}

const handleNoAuthCode = async (codeDetails, fallbackUrl) => {
  let scanResponse = null;

  if (codeDetails) {
    try {
      const params = {
        extended_id: codeDetails.code_id,
        code_space: codeDetails.code_space,
        custom_key: codeDetails.custom_key,
        device: {
          model: props.deviceInfo?.model || "",
          os: props.deviceInfo?.os || "",
          os_version: props.deviceInfo?.osVersion || ""
        },
        app: appConfig.key,
        mobile_app_version: appConfig.version,
        mobile_scan_timestamp: Math.floor(Date.now() / 1000),
        location: {
          positioning: coordinates.value?.accuracy <= 50 ? 4 : coordinates.value?.latitude ? 2 : 0,
          longitude: coordinates.value?.longitude || 0,
          latitude: coordinates.value?.latitude || 0
        }
      }
      scanResponse = await postQueryScan(codeDetails.api_host, params);
    } catch (err) {
      Sentry.captureException(err);
      console.error("Failed to fetch consumer URL for SID code, falling back to raw URL", err);
    }
  }
    const finalUrl = scanResponse?.consumer_url || fallbackUrl;

    sendVideoAuthToNative("NO_AUTH_CODE", {
      content: finalUrl,
      consumer_url: scanResponse?.consumer_url ?? null,
      product: scanResponse?.product ?? null,
      brand: scanResponse?.brand ?? null,
      result: scanResponse?.result ?? null,
      auth_failure_mode: scanResponse?.auth_failure_mode ?? null,
      activation_status: scanResponse?.activation_status ?? null,
      scan_uuid: scanResponse?.uuid ?? null,
      session_id: sessionId
    });

    window.location.href = finalUrl;
}

async function handleEndSessionEvent(event) {
  window.clearTimeout(timer);

  // NO_AUTH_CODE is forwarded from inside its case after the SID lookup
  // resolves, so the native side gets the consumer_url and product info.
  if (event.detail.key !== "AUTH_CANDIDATE_FOUND" && event.detail.key !== "NO_AUTH_CODE") {
    sendVideoAuthToNative(event.detail.key, {
      ...event.detail.options,
      session_id: sessionId,
    });
  }

  switch (event.detail.key) {
    case "AUTH_CANDIDATE_FOUND":
      progress.value = 80;
      __transaction__.makeSpan({
        op: "frame",
        name: "AUTH_CANDIDATE_FOUND"
      });
      amplitude.track("Analysing Secure Graphic", window.amplitudeCommonProperties);
      break;
    case "AUTH_COMPLETE": {
      progress.value = 100;

      const authFailureModeTag = {
        "": "AUTH_GENUINE",
        "auth-failed": "AUTH_COPY",
        unreadable: "AUTH_UNREADABLE",
        inactive: "AUTH_INACTIVE",
        blacklisted: "AUTH_BLACKLISTED",
        untrained: "AUTH_UNTRAINED"
      };

      __transaction__.makeSpan({
        op: "auth",
        name: authFailureModeTag[event.detail.options.auth_failure_mode]
      });

      const amplitudeData = event.detail.options;
      amplitudeData["sessionId"] = sessionId;
      amplitudeData["scanType"] = authFailureModeTag[event.detail.options.auth_failure_mode];

      amplitudeData["timeStartToRead"] = dataLogger.getTimeStartToRead();
      amplitudeData["timeReadToAuthCandidate"] = dataLogger.getTimeReadToAuthCandidate();
      amplitudeData["timeAuthCandidateToResponseReceived"] = dataLogger.getTimeAuthCandidateToResponseReceived();

      exitURL.value = event.detail.options.consumer_url;
      product.value = event.detail.options.product;
      brand.value = event.detail.options.brand;

      const beforeExitActions = [
        withTimeLimit(
          amplitude
            .track("Scan result", {
              ...amplitudeData,
              ...window.amplitudeCommonProperties
            })
            .promise.then(() => amplitude.flush())
        ),
        dataLogger.allLogsUploaded()
      ];
      if (dataLogger.willUploadFrames) {
        beforeExitActions.push(dataLogger.allFramesUploaded());
      }

      __transaction__.end();
      if (event.detail?.options?.auth_failure_mode === "unreadable" && event.detail?.options?.activation_status === 1) {
        resetOverlay();
        openUnreadableModal();
      } else {
        authenticator.stopScanning();
        window.addEventListener("pageshow", () => {
          resetCameraProcess();
        });
        window.addEventListener("pagehide", () => {
          uploading.value = false;
        });

        window.addEventListener("load", () => {
          isAuthenticating.value = false;
        });

        await Promise.allSettled(beforeExitActions);
        window.location.href = exitURL.value;
      }

      break;
    }
    case "NO_AUTH_CODE": {
      __transaction__.makeSpan({
        op: "auth",
        name: "NO_AUTH_CODE"
      });
      __transaction__.end();

      const amplitudeData = {
        sessionId: sessionId,
        scanType: "NO_AUTH_CODE"
      };
      await withTimeLimit(
        amplitude
          .track("Scan result", {
            ...amplitudeData,
            ...window.amplitudeCommonProperties
          })
          .promise.then(() => amplitude.flush())
      );
      window.addEventListener("pagehide", () => {
        uploading.value = false;
      });
      window.addEventListener("load", () => {
        isAuthenticating.value = false;
      }, { once: true });

     await handleNoAuthCode(event.detail.options.codeDetails, event.detail.options.content);

      const finalUrl = scanResponse?.consumer_url || event.detail.options.content;

      sendVideoAuthToNative("NO_AUTH_CODE", {
        content: finalUrl,
        consumer_url: scanResponse?.consumer_url ?? null,
        product: scanResponse?.product ?? null,
        brand: scanResponse?.brand ?? null,
        result: scanResponse?.result ?? null,
        auth_failure_mode: scanResponse?.auth_failure_mode ?? null,
        activation_status: scanResponse?.activation_status ?? null,
        scan_uuid: scanResponse?.uuid ?? null,
        session_id: sessionId
      });

      window.location.href = finalUrl;
      break;
    }

    case "THIRD_PARTY_CODE": {
      uploading.value = false;
      thirdPartyResult.value = event.detail.options.content;
      thirdPartyResultTimeStamp.value = Date.now();
      deviceHeader.value = getDeviceHeader();

      openRescan();

      __transaction__.makeSpan({
        op: "auth",
        name: "THIRD_PARTY_CODE"
      });
      __transaction__.end();

      const amplitudeData = {
        sessionId: sessionId,
        scanType: "THIRD_PARTY_CODE"
      };
      // amplitude flush is done in ThirdPartyCodeResult.vue
      amplitude.track("Scan result", { ...amplitudeData, ...window.amplitudeCommonProperties });
      break;
    }
    case "TIMEOUT": {
      __transaction__.makeSpan({
        op: "auth",
        name: event.detail.options.type === "global" ? "TIMEOUT_GLOBAL" : "TIMEOUT_SDK"
      });
      __transaction__.end();

      // FIXME: No amplitude tracking?
      sessionTimeoutOccurred(event.detail.options.type);
      break;
    }
    case "SDK_ERROR": {
      stopCameraActivity();
      __transaction__.makeSpan({
        op: "auth",
        name: "SDK_ERROR"
      });
      __transaction__.end();

      const amplitudeData = {
        sessionId: sessionId,
        scanType: "SDK_ERROR"
      };
      await withTimeLimit(amplitude.track("Scan result", { ...amplitudeData, ...window.amplitudeCommonProperties }).promise.then(() => amplitude.flush()));

      // TODO: implement this screen
      alert("An unexpected error occurred during the scanning.");
      window.location.reload();
      break;
    }
    case "CODE_NOT_FOUND":
      stopCameraActivity();
      __transaction__.makeSpan({
        op: "auth",
        name: "CODE_NOT_FOUND"
      });
      __transaction__.end();

      alert("The scanned code is not a valid Scantrust code.");
      window.location.reload();
      break;
    case "ERROR":
      stopCameraActivity();
      __transaction__.makeSpan({
        op: "auth",
        name: "API_ERROR"
      });
      __transaction__.end();

      alert("Unable to reach the authentication service. Please try later.");
      window.location.reload();
      break;
    default:
      console.log("SESSION ENDING EVENT RECEIVED:", event);
      __transaction__.makeSpan({
        op: "unhandled-event",
        name: event.detail.key
      });
  }
}

function handleStartSessionEvent(event) {
  dataLogger.recordTimeStartBenchmark();

  console.log("starting session", event);

  startGlobalTimeoutTimer();

  __transaction__.makeSpan({
    op: "auth",
    name: "session start"
  });
}

async function getCamera({ cameraId, isManualSelectCamera, autoStart = true }) {
  try {
    authenticator?.stopProcessing();
    authenticator?.stopScanning();
  } catch (e) {
    Sentry.captureException(e);
  }

  __transaction__.start({
    op: "scanning_session",
    name: "Interactive scanning session",
    sampled: true
  });

  cameraSpan = __transaction__.makeSpan(
    {
      op: "camera",
      name: "open camera"
    },
    false
  );

  const date = new Date();
  console.log("camera start: ", date.getTime());

  if (cameraId === undefined) {
    isLoading.value = true;
    let m = getInstructionsManager();

    if (!settings.value.use_defaults) {
      // We can show messages faster for configured devices (with QR overlay)
      m.setMaxDelay(m.getMaxDelay() / 2);
    }

    document.addEventListener("SDK_EVENT", function (event) {
      console.log("event.detail", event.detail);
      if (event.detail.type === "FEEDBACK") {
        handleSDKFeedbackEvent(event, m);
      } else if (event.detail.type === "END_SESSION") {
        handleEndSessionEvent(event);
      } else if (event.detail.type === "START_SESSION") {
        handleStartSessionEvent(event);
      }
    });
  }

  async function videoAuthenticatorStateChangeCallback() {
    // TODO: handle errors
    selectedCamera.value = authenticator.getSelectedCameraId();

    if (!isCameraReady.value) {
      isCameraReady.value = true;
    }

    try {
      const fallbackSize = await initOverlaySize();
      if (fallbackSize) {
        updateOverlay(fallbackSize, true, "centered");
      }
    } catch (e) {
      console.warn("Failed to initialize overlay on camera start:", e);
      Sentry.captureException(new Error("Failed to initialize overlay on camera start", { cause: e }));
    }

    _initScanEngineCallbacks();

    if (isLoading.value) {
      isLoading.value = false;
    }
  }

  const initOverlaySize = async () => {
    await nextTick();
    const overlayBG = document.getElementById("overlay-external-background");
    const overlayBorder = document.getElementById("overlay-border");
    const videoElement = document.getElementById("video-element");
    const style = window.getComputedStyle(overlayBorder);
    const overlayBorderWidthPx = parseFloat(style.width);

    const videoNaturalWidth = videoElement.videoWidth || 0;
    const videoNaturalHeight = videoElement.videoHeight || 0;
    const containerWidth = overlayBG?.clientWidth || videoElement.clientWidth || innerWidth;
    const containerHeight = overlayBG?.clientHeight || videoElement.clientHeight || innerHeight;

    let previewScale;
    let videoRatio = 0,
      containerRatio = 0;
    if (videoNaturalWidth > 0 && videoNaturalHeight > 0 && containerWidth > 0 && containerHeight > 0) {
      videoRatio = videoNaturalWidth / videoNaturalHeight;
      containerRatio = containerWidth / containerHeight;
      if (videoRatio > containerRatio) {
        previewScale = (videoElement.height || containerHeight) / videoNaturalHeight;
      } else {
        previewScale = (videoElement.width || containerWidth) / videoNaturalWidth;
      }
    }

    const transformationMatrix = new DOMMatrixReadOnly(window.getComputedStyle(videoElement).getPropertyValue("transform"));
    const scaleX = transformationMatrix.a || 1;

    if (overlayBorderWidthPx && previewScale) {
      // The overlay is visible, we need to calculate the size of the overlay
      // this needs to be refactored
      const OVERLAY_RATIO_NUMERATOR = 33;
      const OVERLAY_RATIO_DENOMINATOR = 30;

      const roiWidth = overlayBorderWidthPx / (OVERLAY_RATIO_NUMERATOR / OVERLAY_RATIO_DENOMINATOR);
      const targetWidth = roiWidth / (previewScale * scaleX);

      return {
        width: Math.max(1, Math.floor(targetWidth)),
        height: Math.max(1, Math.floor(targetWidth))
      };
    } else {
      const vts = authenticator.getCamera().getVideoTrackSettings?.() || {};
      return vts.maxStreamSize || (vts.width && vts.height ? { width: vts.width, height: vts.height } : undefined);
    }
  };

  function updateOverlay(targetSize, isInside, position) {
    const overlayBG = document.getElementById("overlay-external-background");
    const overlayQR = document.getElementById("overlay-qr");
    const overlayBorder = document.getElementById("overlay-border");
    const videoElement = document.getElementById("video-element");
    const roi = authenticator.processCodeROI(targetSize, isInside, videoElement, overlayBG);

    const offset = roi.width / 30;
    overlayBorder.style.width = roi.width + 4 * offset + "px";
    overlayBorder.style.height = roi.height + 4 * offset + "px";
    overlayBG.style.clipPath = square_mask_polygon2((roi.width + 4 * offset) / 2);

    // add QR patterns overlay but only if the device is configured
    // to avoid misleading size instruction on un-configured devices.
    if (!settings.value.use_defaults) {
      overlayQR.style.width = roi.width + "px";
      overlayQR.style.height = roi.height + "px";
      overlayQR.style.clipPath = qr_patters_polygon(100, isInside ? 33 : 25, "%");
    }

    isSgInside.value = isInside;
  }

  function _initScanEngineCallbacks() {
    scanDoneBarrierOn = false;
    /** Callback called from within the Wasm SDK
     *
     * This callback is called when a good scan candidate was found.
     *
     * The SDK stops further image analyse once this callback is called.
     *
     * @param {AuthenticationScanCandidate} candidate  the scan candidate found
     */
    cb_authenticationSessionEnded_candidateFound = function (candidate) {
      uploading.value = true;
      authenticator.stopProcessing();
      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;
      sendEvent("AUTH_CANDIDATE_FOUND");
      dataLogger.recordTimeAuthCandidate();
      sendAuthenticationCandidate(candidate, false);
      return true;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when the scanning session timed out while a good enough
     * scan candidate was found.
     *
     * The SDK stops further image analyse once this callback is called.
     *
     * @param {AuthenticationScanCandidate} bestCandidate  the scan candidate found
     */
    cb_authenticationSessionEnded_timeOut = function (bestCandidate) {
      uploading.value = true;
      authenticator.stopProcessing();
      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;
      sendEvent("AUTH_CANDIDATE_FOUND");
      dataLogger.recordTimeAuthCandidate();
      sendAuthenticationCandidate(bestCandidate, true);
      return true;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when a code without authentication feature enable was found.
     *
     * The SDK stops further image analyse once this callback is called.
     *
     * @param {BasicScanCandidate} content  the parsed code content
     */
    cb_authenticationSessionEnded_noAuthCode = function (content) {
      const parsedContent = content.getParsedContent();
      const codeOrigin = content.getCodeOrigin();
      const url = parsedContent.getRawContent();
      const codeDetails = {
        code_id: parsedContent.getCodeID(),
        custom_key: parsedContent.getCustomKey(),
        api_host: codeOrigin.getApiHost(),
        code_space: codeOrigin.getCodeSpace()
      };
      parsedContent.delete();
      codeOrigin.delete();
      authenticator.stopProcessing();

      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;
      sendEvent("NO_AUTH_CODE", { content: url, codeDetails });

      return false;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when a third party code was found.
     *
     * The SDK stops further image analyse once this callback is called.
     *
     * @param {string} content  the code content
     */
    cb_authenticationSessionEnded_thirdPartyContent = function (content) {
      authenticator.stopProcessing();
      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;

      // setResult("thirdPartyContent");
      sendEvent("THIRD_PARTY_CODE", { content });
      return false;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when the backend did not found the code id in its database
     * during the double ping
     *
     * The SDK stops further image analyse once this callback is called.
     *
     * @param {BasicScanCandidate} content  the parsed code content
     */
    cb_authenticationSessionEnded_codeNotFound = function (content) {
      authenticator.stopProcessing();
      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;
      //setResult("CODE_NOT_FOUND");
      sendEvent("CODE_NOT_FOUND");
      return false;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when the session timed out but no good candidate were found.
     *
     * The SDK stops further image analyse once this callback is called.
     *
     * @param {BasicScanCandidate} content  the parsed code content
     */
    cb_authenticationSessionEnded_timeOutWithoutCandidate = function (content) {
      authenticator.stopProcessing();
      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;

      dataLogger.recordTimeAuthCandidate();
      sendEvent("TIMEOUT", { type: "sdk" });
      return false;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when an unexpected error happend durring the scanning session.
     *
     * The SDK stops further image analyse once this callback is called.
     */
    cb_authenticationSessionEnded_error = function () {
      authenticator.stopProcessing();
      if (scanDoneBarrierOn) {
        return false;
      }
      scanDoneBarrierOn = true;

      sendEvent("SDK_ERROR");
      return false;
    };

    // Scan Engine Notification callbacks

    /** Callback called from within the Wasm SDK
     *
     * This callback is called on every analysed frame, it report information about the
     * carrier read and the information about the secure graphic.
     *
     * @param {unsigned int} frameId                id of frame analysed by the scan engine
     * @param {QualityDetails} qualityDetails       object containing the quality details of that frame
     * @param {List<PointF>} carrierCorners         The four corners of the read carrier
     * @param {List<PointF>} carrierPatterns        The four centers of the carrier patterns
     * @param {List<PointF>} secureGraphicMarkers   A list of markers detected around the fingerprint
     * @param {ROI}          secureGraphicROI       The region of interest containing the secure graphic
     * @param {Size}         imageSize              The size of the analysed image
     *
     * The SDK stops further image analyse once this callback is called.
     */
    cb_scanEngineNotification_frameAnalysedNotification = function (frameId, qualityDetails, carrierCorners, carrierPatterns, secureGraphicMarkers, secureGraphicROI, imageSize) {
      dataLogger.frameData.frameId = frameId;

      if (qualityDetails) {
        const sizeResult = qualityDetails.getSizeResult();
        const blurResult = qualityDetails.getBlurResult();
        const glareResult = qualityDetails.getGlareResult();

        dataLogger.frameData.blurScore = blurResult ? blurResult.getScore() : null;
        dataLogger.frameData.blurThreshold = blurResult ? blurResult.getThreshold() : null;
        dataLogger.frameData.glareScore = glareResult ? glareResult.getScore() : null;
        dataLogger.frameData.glareThreshold = glareResult ? glareResult.getThreshold() : null;
        dataLogger.frameData.sizeResult = sizeResult.getSize();
        dataLogger.frameData.sizeLowerLimit = sizeResult.getLowerLimit();
        dataLogger.frameData.sizeUpperLimit = sizeResult.getUpperLimit();
        dataLogger.frameData.carrierPatternCenters = Array.from({ length: carrierPatterns.size() }, (_, i) => i).map((i) => carrierPatterns.get(i));

        console.log(
          "Quality details: \n",
          `  Size result: ${dataLogger.frameData.sizeResult}: [${dataLogger.frameData.sizeLowerLimit} - ${dataLogger.frameData.sizeUpperLimit}]\n`,
          `  Blur result: ${dataLogger.frameData.blurScore}: [${dataLogger.frameData.blurThreshold}]\n`,
          `  Glare result: ${dataLogger.frameData.glareScore}: [${dataLogger.frameData.glareThreshold}]\n`
        );

        qualityDetails.delete();
        sizeResult.delete();
        if (blurResult) {
          blurResult.delete();
        }
        if (glareResult) {
          glareResult.delete();
        }
      }

      if (carrierCorners) {
        carrierCorners.delete();
      }

      if (carrierPatterns) {
        carrierPatterns.delete();
      }

      if (secureGraphicMarkers) {
        secureGraphicMarkers.delete();
      }

      // Note that we are saving cropped images in s3 bucket
      if (secureGraphicROI) {
        dataLogger.frameData.secureGraphicROI = {
          x0: secureGraphicROI.x0,
          y0: secureGraphicROI.y0,
          width: secureGraphicROI.width,
          height: secureGraphicROI.height
        };
      }

      if (imageSize) {
        dataLogger.frameData.imageSize = {
          height: imageSize.height,
          width: imageSize.width
        };
      }

      return true;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when a code is read for the first time, it provides indication
     * about the targeted size of the code. This information can be used by the frontend to
     * display the code size limits on the screen.
     *
     * CodeTargetSize {
     *   Size targetedSize;
     *   Size targetedSizeLowerBounds;
     *   Size targetedSizeUpperBounds;
     *   Size frameSize;
     *   SecureGraphicLayout layout;
     * }
     */
    cb_scanEngineNotification_codeTargetedSizeUpdated = function (targetSize) {
      console.log(
        "Code target size:\n",
        `  targetedSize: [${targetSize.targetedSize.width}, ${targetSize.targetedSize.height}]\n`,
        `  targetedSizeLowerBounds: [${targetSize.targetedSizeLowerBounds.width}, ${targetSize.targetedSizeLowerBounds.height}]\n`,
        `  targetedSizeUpperBounds: [${targetSize.targetedSizeUpperBounds.width}, ${targetSize.targetedSizeUpperBounds.height}]\n`,
        `  frameSize: [${targetSize.frameSize.width}, ${targetSize.frameSize.height}]\n`,
        `  layout: ${targetSize.layout}\n`
      );

      let position;
      if (targetSize.layout === Module.SecureGraphicLayout.BOTTOM) {
        position = "bottom";
      } else if (targetSize.layout === Module.SecureGraphicLayout.CENTERED) {
        position = "centered";
      } else {
        position = "centered";
      }

      updateOverlay(targetSize.targetedSize, targetSize.layout === Module.SecureGraphicLayout.CENTERED, position);

      return false;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when a code is read for the first time, it provide information
     * about the frameId and content of the code.
     *
     * @param {unsigned int} frameId                  id of frame where QR code is read by the scan engine
     * @param {ParsedContent} content  The parsed code content
     */
    cb_scanEngineNotification_carrierReadNotification = function (frameId, content) {
      console.log("Carrier Read frameId: ", frameId);
      // Restart the timer
      resetGlobalTimer();
      startGlobalTimeoutTimer();
      let codeContent = content.getRawContent();
      dataLogger.frameData.codeContent = codeContent;
      if (codeContent !== dataLogger.scanningCodeContent) {
        dataLogger.scanningCodeContent = codeContent;
        dataLogger.recordTimeFirstReadableFrame(true);

        console.log("Scanning code:", content.getCodeID());

        // try {
        //   if (isCameraReady.value && !isAuthenticating.value) {
        //     imageToBlob(image).then(blob => {
        //       console.log("blob", blob);
        //       console.log("blob url", URL.createObjectURL(blob))
        //       videoPreviewImage.value = URL.createObjectURL(blob);
        //     })
        //   }
        // } catch (error) {
        //   console.error(
        //     "Error extracting video preview when QR code detected:",
        //     error,
        //   );
        // }
      }
      content.delete();
      return true;
    };

    // Scan Engine Feedback callbacks

    /** The following `cb_scanEngineFeedback_{feedback}` callback are called from with the
     * Wasm SDK and provide feedback about the current analysed frame. Those call backs can
     * "flicker" a lot and should not be used to directly drive UI but rather be through a
     * proxy manager that smooth / filter the events (e.g. use a timer to prevent too frequent
     * UI update).
     */
    cb_scanEngineFeedback_frameOk = function () {
      sendEvent("FRAME_OK");
      return false;
    };
    cb_scanEngineFeedback_codeNotFound = function () {
      sendEvent("NO_CODE_IN_FRAME");
      return false;
    };
    cb_scanEngineFeedback_codeIsTooSmall = function () {
      sendEvent("CODE_TOO_SMALL");
      return false;
    };
    cb_scanEngineFeedback_codeIsTooBig = function () {
      sendEvent("CODE_TOO_BIG");
      return false;
    };
    cb_scanEngineFeedback_codeIsNotInFocus = function () {
      sendEvent("CODE_IS_BLURRY");
      return false;
    };
    cb_scanEngineFeedback_codeHasGlare = function () {
      sendEvent("CODE_HAS_GLARE");
      return false;
    };
    cb_scanEngineFeedback_secureGraphicIsNotInFrame = function () {
      sendEvent("SG_NOT_IN_FRAME");
      return false;
    };

    /** Callback called from within the Wasm SDK
     *
     * This callback is called when the Wasm SDK request code information. The requested
     * information can be later be provided by calling
     * `Module.provideAuthenticationDetails`
     *
     * Usually we fetch the data from the backend (double ping) and cache the result to avoid
     * repetitive fetch when the same code is scanned many times
     *
     * @param {string} code_id
     * @param {string} api_host
     * @param {string} code_space
     */
    cb_scanEngine_requestAuthenticationDetails = function (code_id, api_host, code_space) {
      console.log(`cb_scanEngine_requestAuthenticationDetails(${code_id}, ${api_host}, ${code_space})`);
      lastScannedCodeDetails = { code_id, api_host, code_space };
      fetchAuthenticationDetails(api_host, code_id, code_space);
      return false;
    };
  }

  async function handleCameraError(error) {
    isLoading.value = false;
    isCameraAccessModalVisible.value = true;
    if (cameraSpan) {
      cameraSpan.end();

      __transaction__.makeSpan({
        op: "camera",
        name: "failed to open camera"
      });

      __transaction__.end();
    }

    if (error) {
      const eventID = Sentry.captureException(error, {
        tags: {
          camera: "failed"
        },
        level: "warning"
      });

      amplitude.track("Failed to open camera", { error: error.toString(), trace_id: eventID });
    }
  }

  const useWorkers = hasSharedArrayBuffer();
  getUsableDeviceWithCache()
    .then((result) => {
      availableCameras.value = result || [];
      authenticator = new VideoAuthenticator(videoAuthenticatorStateChangeCallback, settings.value, prefix.value, availableCameras.value, props.deviceInfo, useWorkers);
      authenticator.onTorchStateChange = (enabled) => {
        isTorchEnabled.value = enabled;
      };

      if (props.deviceInfo.isIos() && desiredCameraType.value === "") {
        const storedIphoneModel = localStorage.getItem("iphoneModel");
        if (availableCameras.value.length > 0 && !storedIphoneModel) {
          if (!skipIphoneModelsPopup.value) {
            showIphoneModels.value = true;
            isCameraOpening.value = false;
            return;
          } else {
            desiredCameraType.value = availableCameras.value.length > 3 ? "triple" : "wide";
          }
        } else {
          const allIphoneModels = Object.values(iphoneModels).flat();
          const matchedIphoneModel = allIphoneModels.find((i) => i.model === storedIphoneModel);
          if (matchedIphoneModel) {
            desiredCameraType.value = matchedIphoneModel.cameraType;
          }
        }
        const desired_camera_name = i18n.t(`iphone_camera.${desiredCameraType.value}`);
        let matching_index = result.findIndex((item) => item.label === desired_camera_name);
        if (matching_index !== -1) {
          // Found in current locale
          cameraId = result[matching_index].deviceId;
        } else {
          // Search in other locales. This is needed when the camera names are cached in the first language.
          // Camera names in other locales excluding current and undefined
          const cameraNameTranslations = Object.entries(i18n.messages.value)
            .map(([locale, message]) => message["iphone_camera"]?.[desiredCameraType.value])
            .filter((_, index) => Object.keys(i18n.messages.value)[index] !== i18n.locale.value)
            .filter((translation) => translation !== undefined && translation !== null);

          for (const translation of cameraNameTranslations) {
            matching_index = result.findIndex((item) => item.label === translation);
            if (matching_index !== -1) {
              cameraId = result[matching_index].deviceId;
              break;
            }
          }
        }
      }

      if (desiredCameraType.value === "triple" && settings.value?.se_min_resolution < 1000) {
        settings.value.se_min_resolution = 1000;
      }

      authenticator.setup(cameraId).then(({ didSucceed, error }) => {
        if (didSucceed) {
          isCameraOpening.value = false;
          authenticator.setDataLogger(dataLogger);
          hasTorch.value = authenticator.getCamera().getCapabilities().torch; // availableCameras.value[0].tracks[0].hasTorch
          // isTorchEnabled.value = hasTorch.value
          if (autoStart) {
            startScanning();
          }
          sendEvent("INIT");
          if (cameraSpan) {
            cameraSpan.end();
          }

          const capabilities = authenticator.getCamera().getCapabilities();
          if (!cameraId) {
            amplitude.track("Click open camera", capabilities);
          } else if (isManualSelectCamera) {
            amplitude.track("Camera start", capabilities);
          }
        } else {
          handleCameraError(error);
        }

        if (!isRetry) {
          let interval = window.setInterval(() => {
            if (counter.value < 5) {
              counter.value = counter.value + 1;
            } else {
              window.clearInterval(interval);
            }
          }, 1000);
        }
      });
    })
    .catch((err) => {
      handleCameraError(err);
    });
}

function selectCamera(index, label, isRetry = false) {
  if (selectedCamera.value === availableCameras.value[index].deviceId) {
    return false;
  } else {
    selectedCamera.value = availableCameras.value[index].deviceId;

    isChangeCameraModalVisible.value = false;
    isVideoRetryModalVisible.value = false;
    isUnreadableModalVisible.value = false;
    isResetCamera = false;
    authenticator.stopScanning();
    wasmM.renewScanEngine();

    resetMessages();
    getCamera({
      cameraId: selectedCamera.value,
      isManualSelectCamera: !isRetry
    });

    if (isRetry) {
      window.amplitudeCommonProperties.attemptId = window.amplitudeCommonProperties.attemptId + 1;
      amplitude.track("1st timeout - select camera", {
        ...window.amplitudeCommonProperties,
        cameraId: label
      });
    } else {
      amplitude.track("Scan overlay - select camera", {
        ...window.amplitudeCommonProperties,
        cameraId: label
      });
    }
    return true;
  }
}

function provideAuthenticationDetails(codeId, details, error) {
  if (error) {
    Module.provideAuthenticationDetails(codeId, Module.AuthenticationDetailsResponseType.Error, null);
    return;
  }
  if (!details.exists) {
    Module.provideAuthenticationDetails(codeId, Module.AuthenticationDetailsResponseType.DetailsNotFound, null);
    return;
  }
  let scanEngineParameters = new Module.ScanEngineParameters(details.auth_settings.blur_threshold_adjustment);

  let authDetails = new Module.AuthenticationDetails(
    scanEngineParameters,
    details.auth_settings.encoded_parameters,
    Module.ActivationStatus.values[details.activation_status],
    Module.TrainingStatus.values[details.training_status]
  );

  Module.provideAuthenticationDetails(codeId, Module.AuthenticationDetailsResponseType.DetailsFound, authDetails);
  authDetails.delete();
  scanEngineParameters.delete();
}

async function fetchAuthenticationDetails(apiHost, codeId, codeSpace) {
  let dataToSend = {
    extended_id: codeId,
    code_space: codeSpace
  };

  fetch("https://" + apiHost + "/api/scan/pre-check/", {
    credentials: "same-origin",
    mode: "cors",
    method: "post",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dataToSend)
  })
    .then((resp) => {
      if (resp.status === 200) {
        return resp.json();
      } else {
        console.log("Status: " + resp.status);
        return Promise.reject(new Error("server", { cause: resp }));
      }
    })
    .then((dataJson) => {
      provideAuthenticationDetails(codeId, dataJson);
      console.log("Double ping success: " + dataJson);
    })
    .catch((err) => {
      provideAuthenticationDetails(codeId, err, true);
      if (err === "server") return;
      console.log("Double ping error: " + err);
    });
}

function stopCameraActivity() {
  resetGlobalTimer();
  isResetCamera = true;
  authenticator.stopProcessing();
}

function resumeScanning() {
  isResetCamera = false;
  resetGlobalTimer(); // FRAME_OK clears the timeout without setting timer to -1; reset so the re-arm below can't no-op on the stale id
  resetMessages();
  wasmM.renewScanEngine();

  if (authenticator?.isCapturing()) {
    scanDoneBarrierOn = false;
    startGlobalTimeoutTimer();
    authenticator.startProcessing();
    return;
  }

  getCamera({ cameraId: selectedCamera.value });
}

function toggleChangeCameraModal() {
  resetGlobalTimer();
  isChangeCameraModalVisible.value = !isChangeCameraModalVisible.value;

  if (isResetCamera) {
    if (!isChangeCameraModalVisible.value) {
      if (isUnreadableModalVisible.value) return;
      resumeScanning();
    } else {
      isUnreadableModalVisible.value = false;
    }
  } else {
    if (isChangeCameraModalVisible.value) {
      stopCameraActivity();
      amplitude.track("Scan overlay - change camera", window.amplitudeCommonProperties);
    } else {
      if (isUnreadableModalVisible.value) return;
      resumeScanning();
      amplitude.track("Scan overlay - close change camera", window.amplitudeCommonProperties);
    }
  }
}

function openVideoRetryModal() {
  isVideoRetryModalVisible.value = true;
  stopCameraActivity();
  amplitude.track("1st timeout", window.amplitudeCommonProperties);
}

function closeVideoRetryModal() {
  isVideoRetryModalVisible.value = false;
  resumeScanning();
  amplitude.track("Close change camera pop up");
  amplitude.track("1st timeout - close pop up", window.amplitudeCommonProperties);
}

function toggleCameraAccessModal() {
  isCameraAccessModalVisible.value = !isCameraAccessModalVisible.value;

  if (!isCameraAccessModalVisible.value) {
    amplitude.track("Close site camera guidance pop up");
  }
}

function resetMessages() {
  progress.value = 0;
  message.value = "scan.align";
  amplitude.track("Place QR in the frame", window.amplitudeCommonProperties);
}

function resetOverlay() {
  const overlayQR = document.getElementById("overlay-qr");
  const overlayBorder = document.getElementById("overlay-border");
  const overlayBG = document.getElementById("overlay-external-background");
  overlayBG.removeAttribute("style");
  overlayQR.removeAttribute("style");
  overlayBorder.removeAttribute("style");
}

function openUnreadableModal() {
  isAuthenticating.value = false;
  uploading.value = false;
  isUnreadableModalVisible.value = true;
  stopCameraActivity();
}

function closeUnreadableModal() {
  isAuthenticating.value = false;
  uploading.value = false;
  isUnreadableModalVisible.value = false;

  exitURL.value = "";
  product.value = {};
  brand.value = {};

  resumeScanning();
}

function openRescan() {
  uploading.value = false;
  isResponseError.value = false;
  videoPreviewImage.value = "";
  isThirdPartyCode.value = true;
  stopCameraActivity();
}

function closeRescan() {
  uploading.value = false;
  isResponseError.value = false;
  videoPreviewImage.value = "";
  isThirdPartyCode.value = false;

  resumeScanning();
  wechatInitializeClick();
}

function resetGlobalTimer() {
  window.clearTimeout(timer);
  timer = -1;
}

function handleInitialized({ installId: currentInstallId, prefix: currentPrefix, settings: currentSettings, coordinates: currentCoordinates }) {
  installId.value = currentInstallId;
  prefix.value = currentPrefix;
  settings.value = currentSettings;

  isCameraOpening.value = true;

  coordinates.value = currentCoordinates;
  startCameraProcess();
  try {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set("Configured", settings.value.use_defaults ? "No" : "Yes");
    identifyEvent.set("Device model", props.deviceInfo?.model);
    identifyEvent.set("Browser", props.deviceInfo?.browser);
    identifyEvent.set("Browser Version", props.deviceInfo?.browserVersion);
    identifyEvent.set("Browser Engine", props.deviceInfo?.browserEngine);
    identifyEvent.set("First app version", __INITIAL__APP_VERSION__);
    identifyEvent.set("App version", __APP_VERSION__);
    identifyEvent.set("Campaign id", window.amplitudeUserProperties.campaign_id);
    identifyEvent.set("Brand id", window.amplitudeUserProperties.brand_id);
    identifyEvent.set("Company id", window.amplitudeUserProperties.company_id);
    identifyEvent.set("Extended id", window.amplitudeUserProperties.extended_id);
    identifyEvent.set("Product id", window.amplitudeUserProperties.product_id);
    identifyEvent.set("Scan App", window.amplitudeUserProperties.scanApp);
    identifyEvent.set("Scan uid", window.amplitudeUserProperties.scan_uid);
    identifyEvent.set("Scan url", window.amplitudeUserProperties.scanningURL);
    identifyEvent.set("Session id", window.amplitudeUserProperties.sessionId);
    amplitude.identify(identifyEvent);
  } catch (err) {}
}

function startScanning() {
  // if (settings.value.use_defaults && counter.value < 5) {
  //   return;
  // }
  if (settings.value.use_defaults) {
    message.value = "scan.align";
    amplitude.track("Start scanning", window.amplitudeCommonProperties);
  } else {
    message.value = "scan.align";
    amplitude.track("Tap to start", window.amplitudeCommonProperties);
  }
  if (!shouldDisableTorchAtStart() && hasTorch.value) {
    if (brandingTheme.value && Object.keys(brandingTheme.value).includes("torchControlInVideoAuth")) {
      authenticator.setTorch({ isEnabled: brandingTheme.value.torchControlInVideoAuth });
    } else {
      authenticator.setTorch({ isEnabled: true });
    }
  }
  authenticator.startProcessing();
}

function displayVideo(show) {
  if (show) {
    document.querySelector("#video-element").style.display = "block";
  } else {
    document.querySelector("#video-element").style.display = "none";
  }
}

function handleTorchButtonClick() {
  // Toggle torch
  const newTorchState = !isTorchEnabled.value;
  authenticator.setTorch({ isEnabled: newTorchState });
  if (isTorchEnabled.value) {
    amplitude.track("Turn on torch", window.amplitudeCommonProperties);
  } else {
    amplitude.track("Turn off torch", window.amplitudeCommonProperties);
  }
}

function hasBlockingOverlay() {
  return isChangeCameraModalVisible.value
    || isVideoRetryModalVisible.value
    || isUnreadableModalVisible.value
    || showIphoneModels.value
    || isThirdPartyCode.value
    || isResponseError.value;
}

function resumeProcessingIfClear() {
  if (!hasBlockingOverlay()) {
    authenticator?.startProcessing();
  }
}

function handleInstructionToggle(visible) {
  if (visible) {
    stopCameraActivity();
  } else {
    resumeProcessingIfClear();
  }
}

function handleInstructionStartScan() {
  if (isThirdPartyCode.value || isResponseError.value) {
    closeRescan();
  } else {
    resumeProcessingIfClear();
  }
}

const handleCameraRetry = () => {
  isVideoRetryModalVisible.value = false;
  isResetCamera = false;
  if (props.deviceInfo?.isIos()) {
    stopCameraActivity();
    showIphoneModels.value = true;
  } else {
    toggleChangeCameraModal();
  }
};

const closeIphoneModelsPicker = (isSkipped) => {
  showIphoneModels.value = false;
  if (isSkipped) {
    skipIphoneModelsPopup.value = true;
    if (isUnreadableModalVisible.value) return;
    resumeScanning();
  }
};

const selectedIphoneCamera = () => {
  if (isUnreadableModalVisible.value) {
    isUnreadableModalVisible.value = false;
  }
  isResetCamera = false;
  desiredCameraType.value = "";
  resumeScanning();
};

const startCameraProcess = () => {
  isCameraOpening.value = true;
  getCamera({ autoStart: true });
};

function resetCameraProcess() {
  resetGlobalTimer();
  try {
    authenticator?.stopProcessing();
    authenticator?.stopScanning();
  } catch (e) {
    Sentry.captureException(e);
  }

  // Reset engine state
  wasmM.renewScanEngine();

  // Clear UI state
  uploading.value = false;
  isAuthenticating.value = false;
  videoPreviewImage.value = "";
  resetMessages();
  resetOverlay();
  // Re-open camera
  getCamera({ cameraId: selectedCamera.value, autoStart: true });
}
</script>

<template>
<Launch
  v-if="!isCameraReady && !isCameraAccessModalVisible"
  :device-info="deviceInfo"
  :on-initialized="handleInitialized"
  :is-camera-opening="isCameraOpening"
  :toggle-camera-access-modal="toggleCameraAccessModal"
  :has-wasm-crashed="hasWasmCrashed"
  @wechat-initialize-click="wechatInitializeClick"
/>
<Navbar v-if="isCameraReady || isCameraAccessModalVisible" @instruction-toggle="handleInstructionToggle" @instruction-start-scan="handleInstructionStartScan" />

<IphoneModelsPicker :open="showIphoneModels" :cameras="availableCameras" @close="closeIphoneModelsPicker" @select="selectedIphoneCamera" />

<div v-show="!uploading && !isThirdPartyCode && !isResponseError" class="text_all fixed top-0 right-0 bottom-0 left-0" :class="[!isCameraReady ? 'hidden' : 'flex']">
  <div id="result"></div>
  <div class="fixed top-0 left-0 m-0 h-dvh w-screen bg-[#777777] p-0">
    <div id="video-container">
      <video
        id="video-element"
        class="absolute inset-0 h-dvh scale-100 transform object-cover transition duration-200 landscape:top-0 landscape:left-0 landscape:w-screen"
        autoplay
        playsinline
        muted
      />
    </div>
  </div>
  <div id="overlay-external-background" class="fixed inset-0 z-[9999] bg-black/40" :class="{ 'message-box-clip': isScanning && progress < 80 }">
    <div
      class="message-box"
      :class="{
        'message-box-outside': !isSgInside,
        'message-box-default': settings.use_defaults,
        'message-box-start': isScanning && settings.use_defaults
      }"
    >
      <div v-if="isScanning" class="absolute left-1/2 top-20 z-50 w-max max-w-[90vw] -translate-x-1/2 rounded bg-white/80 px-5 py-1">
        <div class="flex items-center justify-center text-center text-2xl font-normal text-black">
          {{ $t(message) }}
        </div>
      </div>
      <div v-if="!isAuthenticating && isScanning && !settings.use_defaults && progress < 80" class="absolute right-0 bottom-[26px] left-0 mx-2.5 block landscape:hidden">
        <div v-if="hasTorch" class="torch" @click="handleTorchButtonClick()">
          <button v-if="!isTorchEnabled" class="torch-btn torch-btn-on select-none">
            <svg width="28" height="28" viewBox="0 0 55 55" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="27.5" cy="27.5" r="27.5" fill="#222" fill-opacity="0.75" />
              <path
                d="M38.7205 21.7545C38.7166 21.7665 38.7137 21.7795 38.7075 21.791C38.6657 21.8986 38.6042 21.9961 38.5322 22.084C38.5259 22.0926 38.524 22.1032 38.5163 22.1109L33.6809 27.6279V46.8823C33.6809 47.4774 33.1977 47.9601 32.6031 47.9601H22.3957C21.8001 47.9601 21.3179 47.4774 21.3179 46.8823V27.6279L16.4815 22.1109C16.4748 22.1032 16.4729 22.0926 16.4657 22.084C16.3931 21.9966 16.3326 21.8991 16.2904 21.791C16.286 21.779 16.2836 21.7665 16.2793 21.7545C16.2399 21.6426 16.2145 21.5249 16.2145 21.4001V17.0564C16.2145 16.4613 16.6972 15.9786 17.2922 15.9786H37.7076C38.3031 15.9786 38.7853 16.4613 38.7853 17.0564V21.4001C38.7853 21.5249 38.7598 21.6431 38.7205 21.7545ZM27.4996 30.8919C26.9046 30.8919 26.4219 31.3745 26.4219 31.9696V36.8824C26.4219 37.477 26.9046 37.9602 27.4996 37.9602C28.0947 37.9602 28.5769 37.477 28.5769 36.8824V31.9696C28.5774 31.3745 28.0952 30.8919 27.4996 30.8919ZM36.6293 18.1346H18.3705V20.3228H36.6293V18.1346Z"
                fill="#fff"
              />
              <path
                d="M27.4523 7.04004C26.9586 7.04004 26.5582 7.52836 26.5582 8.13038V13.1006C26.5582 13.7021 26.9586 14.1909 27.4523 14.1909C27.9459 14.1909 28.3459 13.7021 28.3459 13.1006V8.13038C28.3463 7.52836 27.9463 7.04004 27.4523 7.04004Z"
                fill="#fff"
              />
              <path
                d="M36.3906 7.51917C35.9631 7.27235 35.3722 7.49504 35.0712 8.0164L32.5861 12.3207C32.2853 12.8416 32.3877 13.4652 32.8152 13.712C33.2427 13.9588 33.8335 13.7355 34.1343 13.2146L36.6194 8.91026C36.9207 8.3891 36.8185 7.76619 36.3906 7.51917Z"
                fill="#fff"
              />
              <path
                d="M18.5133 7.51891C18.0858 7.76574 17.9832 8.38884 18.2842 8.9102L20.7693 13.2145C21.07 13.7354 21.6612 13.9586 22.0887 13.7117C22.5162 13.4649 22.6182 12.8416 22.3175 12.3206L19.8324 8.01635C19.5317 7.49478 18.9411 7.27189 18.5133 7.51891Z"
                fill="#fff"
              />
            </svg>
            {{ $t("torch.on") }}
          </button>
          <button v-else class="torch-btn select-none">
            <svg width="24" height="24" viewBox="0 0 16 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14.5767 9.28791C14.6474 8.90295 14.6843 8.50619 14.6843 8.1008C14.6843 7.30555 14.5422 6.54349 14.282 5.83862C14.214 5.81321 14.1403 5.79932 14.0634 5.79932H11.8907L10.6421 7.04791H13.439V8.31514H9.37483L4.57171 13.1183V13.5655C5.48459 14.1648 6.55667 14.5415 7.71078 14.6185C7.82371 14.5057 7.9796 14.436 8.15173 14.436C8.324 14.436 8.47989 14.5058 8.59276 14.6185C9.7467 14.5416 10.8186 14.1651 11.7315 13.566V12.5457L14.5318 9.35068C14.5344 9.34811 14.5359 9.34499 14.5373 9.34185C14.5384 9.33954 14.5395 9.33722 14.541 9.3351C14.5535 9.31987 14.5654 9.30414 14.5767 9.28791ZM4.57171 14.0457V23.6966C4.57171 24.0412 4.85097 24.3208 5.19587 24.3208H11.1073C11.4516 24.3208 11.7315 24.0412 11.7315 23.6966V14.0462C10.8536 14.5758 9.84898 14.9165 8.77381 15.012C8.77501 15.0279 8.77562 15.044 8.77561 15.0602V17.9053C8.77561 18.2497 8.49635 18.5295 8.15173 18.5295C7.8071 18.5295 7.52757 18.2497 7.52757 17.9053V15.0602C7.52757 15.044 7.52819 15.0279 7.5294 15.012C6.4542 14.9164 5.44953 14.5755 4.57171 14.0457ZM3.17834 10.9563L5.81949 8.31514H2.86473V7.04791H7.08672L8.33532 5.79932H2.2403C2.16346 5.79932 2.08987 5.81321 2.02188 5.83862C1.76168 6.54349 1.61957 7.30555 1.61957 8.1008C1.61957 8.50716 1.65667 8.90486 1.72768 9.29068C1.73838 9.30596 1.74971 9.32077 1.76161 9.3351C1.7637 9.33761 1.76502 9.3404 1.76631 9.34312C1.76759 9.34582 1.76884 9.34846 1.77079 9.35068L3.17834 10.9563Z"
                fill="#222222"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M15.0939 8.10085C15.0939 11.9348 11.9859 15.0429 8.15188 15.0429C6.29439 15.0429 4.60729 14.3134 3.36147 13.1252L13.1762 3.31044C14.3644 4.55626 15.0939 6.24336 15.0939 8.10085ZM2.79048 12.5111L12.5621 2.73945C11.3629 1.75187 9.82666 1.1588 8.15188 1.1588C4.31789 1.1588 1.20983 4.26686 1.20983 8.10085C1.20983 9.77564 1.8029 11.3119 2.79048 12.5111ZM15.9319 8.10085C15.9319 12.3977 12.4487 15.8809 8.15188 15.8809C3.85507 15.8809 0.371826 12.3977 0.371826 8.10085C0.371826 3.80405 3.85507 0.320801 8.15188 0.320801C12.4487 0.320801 15.9319 3.80405 15.9319 8.10085Z"
                fill="#222222"
              />
            </svg>
            {{ $t("torch.off") }}
          </button>
        </div>
      </div>
      <div v-if="!isAuthenticating && isScanning && settings.use_defaults" class="absolute bottom-[26px] left-1/2 flex w-[96%] -translate-x-1/2 gap-2 overflow-hidden">
        <button
          v-if="availableCameras.length > 1"
          class="flex h-[70px] flex-1 flex-col items-center justify-center rounded bg-[var(--primary)] text-center text-[16px] leading-[18.75px] font-normal text-[var(--text-button)]"
          @click="handleCameraRetry()"
        >
          <svg class="fill-[var(--text-button)]" width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_2839_1049)">
              <path
                d="M12.2866 6.93311V9.93311L16.2866 5.93311L12.2866 1.93311V4.93311C7.86662 4.93311 4.28662 8.51311 4.28662 12.9331C4.28662 14.5031 4.74662 15.9631 5.52662 17.1931L6.98662 15.7331C6.53662 14.9031 6.28662 13.9431 6.28662 12.9331C6.28662 9.62311 8.97662 6.93311 12.2866 6.93311ZM19.0466 8.67311L17.5866 10.1331C18.0266 10.9731 18.2866 11.9231 18.2866 12.9331C18.2866 16.2431 15.5966 18.9331 12.2866 18.9331V15.9331L8.28662 19.9331L12.2866 23.9331V20.9331C16.7066 20.9331 20.2866 17.3531 20.2866 12.9331C20.2866 11.3631 19.8266 9.90311 19.0466 8.67311Z"
              />
            </g>
            <defs>
              <clipPath id="clip0_2839_1049">
                <rect width="24" height="24" fill="white" transform="translate(0.286621 0.933105)" />
              </clipPath>
            </defs>
          </svg>
          {{ isIos ? $t("select_phone_model") : $t("change_camera") }}
        </button>
        <div
          v-if="hasTorch"
          class="flex h-[70px] flex-1 flex-col items-center justify-center rounded bg-[var(--primary)] text-center text-[16px] leading-[18.75px] font-normal text-[var(--text-button)]"
          @click="handleTorchButtonClick()"
        >
          <button v-if="!isTorchEnabled" class="flex h-full w-full flex-col items-center justify-center rounded bg-[var(--text-button)] text-[var(--primary)]">
            <svg class="fill-[var(--primary)]" width="24" height="24" viewBox="0 0 17 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16.5274 9.0081C16.5247 9.01514 16.5226 9.02275 16.5182 9.02951C16.4886 9.09261 16.445 9.14979 16.3939 9.20134C16.3895 9.20641 16.3882 9.21261 16.3827 9.21712L12.9549 12.4529V23.7458C12.9549 24.0948 12.6124 24.3779 12.1909 24.3779H4.95511C4.53293 24.3779 4.19111 24.0948 4.19111 23.7458V12.4529L0.762661 9.21712C0.757895 9.21261 0.756533 9.20641 0.751426 9.20134C0.700016 9.15007 0.657118 9.09289 0.627157 9.02951C0.624093 9.02247 0.622391 9.01514 0.619327 9.0081C0.591409 8.94247 0.573364 8.87345 0.573364 8.80021V6.25261C0.573364 5.9036 0.915528 5.6205 1.33736 5.6205H15.8094C16.2315 5.6205 16.5734 5.9036 16.5734 6.25261V8.80021C16.5734 8.87345 16.5553 8.94275 16.5274 9.0081ZM8.57319 14.3672C8.15136 14.3672 7.8092 14.6503 7.8092 14.9994V17.8808C7.8092 18.2295 8.15136 18.5129 8.57319 18.5129C8.99503 18.5129 9.33685 18.2295 9.33685 17.8808V14.9994C9.33719 14.6503 8.99537 14.3672 8.57319 14.3672ZM15.045 6.88501H2.1017V8.16839H15.045V6.88501Z"
              />
              <path
                d="M8.53956 0.37793C8.18962 0.37793 7.90578 0.664334 7.90578 1.01742V3.93248C7.90578 4.28528 8.18962 4.57197 8.53956 4.57197C8.88949 4.57197 9.17305 4.28528 9.17305 3.93248V1.01742C9.17334 0.664334 8.88977 0.37793 8.53956 0.37793Z"
              />
              <path
                d="M14.8758 0.658914C14.5727 0.514151 14.1538 0.644762 13.9404 0.950547L12.1788 3.47506C11.9656 3.7806 12.0382 4.1463 12.3412 4.29106C12.6443 4.43583 13.0631 4.30485 13.2763 3.99931L15.0379 1.4748C15.2515 1.16913 15.1791 0.803794 14.8758 0.658914Z"
              />
              <path
                d="M2.20296 0.658802C1.8999 0.803565 1.82717 1.16902 2.04055 1.47481L3.80218 3.99932C4.01538 4.30486 4.43445 4.43571 4.7375 4.29095C5.04056 4.14619 5.11287 3.7806 4.89967 3.47506L3.13804 0.950551C2.9249 0.644649 2.50625 0.513922 2.20296 0.658802Z"
              />
            </svg>
            {{ $t("torch.on") }}
          </button>
          <button v-else class="flex h-full w-full flex-col items-center justify-center rounded">
            <svg class="fill-[var(--text-button)]" width="24" height="24" viewBox="0 0 16 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14.5767 9.28791C14.6474 8.90295 14.6843 8.50619 14.6843 8.1008C14.6843 7.30555 14.5422 6.54349 14.282 5.83862C14.214 5.81321 14.1403 5.79932 14.0634 5.79932H11.8907L10.6421 7.04791H13.439V8.31514H9.37483L4.57171 13.1183V13.5655C5.48459 14.1648 6.55667 14.5415 7.71078 14.6185C7.82371 14.5057 7.9796 14.436 8.15173 14.436C8.324 14.436 8.47989 14.5058 8.59276 14.6185C9.7467 14.5416 10.8186 14.1651 11.7315 13.566V12.5457L14.5318 9.35068C14.5344 9.34811 14.5359 9.34499 14.5373 9.34185C14.5384 9.33954 14.5395 9.33722 14.541 9.3351C14.5535 9.31987 14.5654 9.30414 14.5767 9.28791ZM4.57171 14.0457V23.6966C4.57171 24.0412 4.85097 24.3208 5.19587 24.3208H11.1073C11.4516 24.3208 11.7315 24.0412 11.7315 23.6966V14.0462C10.8536 14.5758 9.84898 14.9165 8.77381 15.012C8.77501 15.0279 8.77562 15.044 8.77561 15.0602V17.9053C8.77561 18.2497 8.49635 18.5295 8.15173 18.5295C7.8071 18.5295 7.52757 18.2497 7.52757 17.9053V15.0602C7.52757 15.044 7.52819 15.0279 7.5294 15.012C6.4542 14.9164 5.44953 14.5755 4.57171 14.0457ZM3.17834 10.9563L5.81949 8.31514H2.86473V7.04791H7.08672L8.33532 5.79932H2.2403C2.16346 5.79932 2.08987 5.81321 2.02188 5.83862C1.76168 6.54349 1.61957 7.30555 1.61957 8.1008C1.61957 8.50716 1.65667 8.90486 1.72768 9.29068C1.73838 9.30596 1.74971 9.32077 1.76161 9.3351C1.7637 9.33761 1.76502 9.3404 1.76631 9.34312C1.76759 9.34582 1.76884 9.34846 1.77079 9.35068L3.17834 10.9563Z"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M15.0939 8.10085C15.0939 11.9348 11.9859 15.0429 8.15188 15.0429C6.29439 15.0429 4.60729 14.3134 3.36147 13.1252L13.1762 3.31044C14.3644 4.55626 15.0939 6.24336 15.0939 8.10085ZM2.79048 12.5111L12.5621 2.73945C11.3629 1.75187 9.82666 1.1588 8.15188 1.1588C4.31789 1.1588 1.20983 4.26686 1.20983 8.10085C1.20983 9.77564 1.8029 11.3119 2.79048 12.5111ZM15.9319 8.10085C15.9319 12.3977 12.4487 15.8809 8.15188 15.8809C3.85507 15.8809 0.371826 12.3977 0.371826 8.10085C0.371826 3.80405 3.85507 0.320801 8.15188 0.320801C12.4487 0.320801 15.9319 3.80405 15.9319 8.10085Z"
              />
            </svg>
            {{ $t("torch.off") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="isScanning" id="overlay-qr" class="overlay-qr" :class="{ 'overlay-qr-stay-still': progress >= 75 }"></div>
  <div v-if="isScanning" id="overlay-border" class="overlay-box"></div>

  <ChangeCameraModal :visible="isChangeCameraModalVisible" :cameras="availableCameras" :toggle="toggleChangeCameraModal" :select="selectCamera" :selected-camera="selectedCamera" />
  <VideoAuthRetryModal
    :visible="isVideoRetryModalVisible"
    :toggle="closeVideoRetryModal"
    :device-info="deviceInfo"
    :cameras-count="availableCameras.length"
    @change-camera="handleCameraRetry"
    />
</div>
<AnalyzingScreen v-if="uploading" :preview="videoPreviewImage" />

<ThirdPartyCodeResult
  v-show="isThirdPartyCode"
  ref="thirdPartyCodeResultRef"
  :result="thirdPartyResult"
  :result_timestamp="thirdPartyResultTimeStamp"
  :toggle="closeRescan"
  :app_slug="appConfig.key"
  :install_id="installId"
  :device="props.deviceInfo.model"
  :device_header="deviceHeader"
  :os="props.deviceInfo.os"
  :os_version="props.deviceInfo.osVersion"
  :coordinates="coordinates"
/>

<ErrorModal v-if="isResponseError" :toggle="closeRescan" :error-code="errorCode" />

<CameraAccessModal v-if="isCameraAccessModalVisible" :visible="isCameraAccessModalVisible" :toggle-camera-access-modal="toggleCameraAccessModal" />

<Footer :is-camera-ready="isCameraReady" :version="appConfig.version" />

<Unreadable
  :visible="isUnreadableModalVisible"
  :toggle="closeUnreadableModal"
  :exit-u-r-l="exitURL"
  :product="product"
  :brand="brand"
  :photo="videoPreviewImage"
  :is-hybrid="!isSgInside"
  :is-ios="isIos"
  :cameras-count="availableCameras.length"
  @select="handleCameraRetry"
/>

<OrientationModal :is-camera-ready="isCameraReady" :display-video="displayVideo" />
</template>
